class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      // Ignore clicks while another cart action is still running
      if (window.__cartBusy) return;
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
  if (event.target.closest('.cart-autoship-wrapper')) return;

  const lineIndex =
    event.target.dataset.index ||
    event.target.closest('[id^="CartItem-"]')?.id.replace('CartItem-', '') ||
    event.target.closest('[id^="CartDrawer-Item-"]')?.id.replace('CartDrawer-Item-', '');

  if (!lineIndex) {
    return;
  }

  const qty = parseInt(event.target.value, 10);

  if (isNaN(qty)) {
    return;
  }

  this.updateQuantity(
    lineIndex,
    qty,
    document.activeElement.getAttribute("name")
  );
}

  async onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {

      while (window.__cartDrawerRendering) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      window.__cartDrawerRendering = true;

      try {
        const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const selectors = ["cart-drawer-items", ".cart-drawer-footer"];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
        if (typeof AOS !== "undefined") {
          AOS.init();
        }

        const nextIsEmpty = !html.querySelector('.cart-item-row');
        const cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl) cartDrawerEl.classList.toggle('is-empty', nextIsEmpty);
        if (typeof window.updateCartSectionVisibility === 'function') window.updateCartSectionVisibility();

        if (typeof window.updateShippingBanner === 'function') window.updateShippingBanner(html);
      } catch (e) {
        console.error(e);
      } finally {
        window.__cartDrawerRendering = false;
      }
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const sourceQty = html.querySelector("cart-items");
          if (sourceQty) {
            this.innerHTML = sourceQty.innerHTML;
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const mainCartItems = document.getElementById("main-cart-items");
    const mainCartFooter = document.getElementById("main-cart-footer");

    return [
      mainCartItems && mainCartItems.dataset.id && {
        id: "main-cart-items",
        section: mainCartItems.dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      mainCartFooter && mainCartFooter.dataset.id && {
        id: "main-cart-footer",
        section: mainCartFooter.dataset.id,
        selector: ".js-contents",
      },
    ].filter(Boolean);
  }

  async updateQuantity(line, quantity, name) {
    const isDrawer = this.tagName === "CART-DRAWER-ITEMS";

    // Only allow one cart action at a time. Line indexes shift after a
    // removal, so a second queued request would target the wrong line.
    if (window.__cartBusy) {
      const qtyEl =
        document.getElementById(`Quantity-${line}`) ||
        document.getElementById(`Drawer-quantity-${line}`);
      if (qtyEl) qtyEl.value = qtyEl.getAttribute("value");
      return;
    }
    window.__cartBusy = true;

    if (isDrawer) {

      while (window.__cartDrawerRendering) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      window.__cartDrawerRendering = true;
    }

    this.enableLoading(line);

    const sectionsToRender = this.getSectionsToRender();
    const sectionIds = sectionsToRender.map((section) => section.section).filter(Boolean);

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionIds,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          console.warn('[Cart] API returned errors:', parsedState.errors);
        }

        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const el = document.getElementById(section.id);
          if (!el) return;
          const sectionHTML = parsedState.sections[section.section];
          if (!sectionHTML) return;
          const parsed = this.getSectionInnerHTML(sectionHTML, section.selector);
          if (parsed === null) return;
          const elementToReplace = el.querySelector(section.selector) || el;
          elementToReplace.innerHTML = parsed;
        });

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          quantityElement &&
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const drawerIsOpen = cartDrawerWrapper?.classList.contains('active');

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          drawerIsOpen
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && drawerIsOpen) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer-inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && drawerIsOpen) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item-name")
          );
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });

        setTimeout(() => {
          document.dispatchEvent(new Event('cart:updated'));
        }, 400);
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        if (isDrawer) window.__cartDrawerRendering = false;
        window.__cartBusy = false;
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item-error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "change",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

class TermsCheckbox extends HTMLElement {
  constructor() {
    super();
    this.checkbox = null;
    this.checkoutButtons = null;
  }

  connectedCallback() {
    this.checkbox = this.querySelector("#termsCheckbox");
    this.checkoutButtons = document.querySelectorAll(".cart-checkout-button");

    this.restoreCheckboxState();
    this.checkbox.addEventListener("change", this.updateState.bind(this));

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.reinitializeCheckbox.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  updateState() {
    if (this.checkoutButtons.length) {
      this.checkoutButtons.forEach((button) => {
        if (this.checkbox.checked) {
          button.removeAttribute("disabled");
        } else {
          button.setAttribute("disabled", "disabled");
        }
      });
      if (this.checkbox.checked) {
        this.checkoutButtons[0].focus();
      }
    }
    this.saveCheckboxState();
  }

  saveCheckboxState() {
    localStorage.setItem(
      "termsCheckboxState",
      this.checkbox.checked ? "checked" : "unchecked"
    );
  }

  restoreCheckboxState() {
    const savedState = localStorage.getItem("termsCheckboxState");
    this.checkbox.checked = savedState === "checked";
    this.updateState();
  }

  reinitializeCheckbox() {
    this.checkbox = this.querySelector("#termsCheckbox");
    this.restoreCheckboxState();

    if (typeof AOS !== "undefined") {
      AOS.init();
    }
  }
}

customElements.define("terms-checkbox", TermsCheckbox);


class CustomDropdown extends HTMLElement {
  constructor() {
    super();
    this.dropdownHeader = this.querySelector(".dropdown-header");
    this.dropdownContent = this.querySelector(".dropdown-content");

    if (this.dropdownHeader && this.dropdownContent) {
      this.dropdownHeader.addEventListener("click", () =>
        this.toggleDropdown()
      );
    }
  }
  toggleDropdown() {
    const isOpen = this.dropdownContent.classList.contains("open");

    document
      .querySelectorAll("custom-dropdown .dropdown-content")
      .forEach((content) => {
        content.classList.remove("open");
      });
    document
      .querySelectorAll("custom-dropdown .dropdown-header")
      .forEach((header) => {
        header.classList.remove("open");
      });

    if (!isOpen) {
      this.dropdownContent.classList.add("open");
      this.dropdownHeader.classList.add("open");
    }
    if (typeof AOS !== "undefined") {
      AOS.init();
    }
  }
  connectedCallback() {}
  disconnectedCallback() {
    if (this.dropdownHeader) {
      this.dropdownHeader.removeEventListener("click", this.toggleDropdown);
    }
  }
}
customElements.define("custom-dropdown", CustomDropdown);

class ShippingMessage extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.render();
  }

  render() {
    try {
      const threshold = parseFloat(this.getAttribute("data-threshold"));
      const cartTotal = parseFloat(this.getAttribute("data-cart-total"));
      const messageBefore = this.getAttribute("data-message-before");
      const messageAfter = this.getAttribute("data-message-after");
      const messageSuccess = this.getAttribute("data-message-success");

      const currencyRate = (window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
      const currencyCode = (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD";

      const convertedThreshold = threshold * currencyRate;
      let remainingAmount = Math.max(0, convertedThreshold - cartTotal);

      remainingAmount = remainingAmount.toFixed(2);

      function formatCurrency(amount, currency) {
        const options = { style: "currency", currency: currency };
        return new Intl.NumberFormat("en-US", options).format(amount);
      }

      const messageElement = document.createElement("div");

      if (remainingAmount > 0) {
        messageElement.innerHTML = `${messageBefore}<span class="amount-bold" id="free-shipping-amount">${formatCurrency(remainingAmount, currencyCode)}</span>${messageAfter}`;
      } else {
        messageElement.innerHTML = `${messageSuccess}`;
      }

      this.innerHTML = "";
      this.appendChild(messageElement);
    } catch (e) {
      console.warn("[ShippingMessage] render failed, leaving previous content in place", e);
    }
  }
}

customElements.define("shipping-message", ShippingMessage);

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      this.minusBtn = this.querySelector('[name="minus"]');
      this.plusBtn = this.querySelector('[name="plus"]');

      if (!this.input) return;

      this.syncMinFromDataMin();

      this._minObserver = new MutationObserver(() => this.syncMinFromDataMin());
      this._minObserver.observe(this.input, { attributes: true, attributeFilter: ['data-min'] });

      this.minusBtn?.addEventListener('click', () => this.changeQty(-1));
      this.plusBtn?.addEventListener('click', () => this.changeQty(1));
    }

    disconnectedCallback() {
      if (this._minObserver) this._minObserver.disconnect();
    }

    syncMinFromDataMin() {
      const attrMin = parseInt(this.input.getAttribute('min'), 10) || 0;
      const dataMin = parseInt(this.input.getAttribute('data-min'), 10) || 0;
      if (dataMin > attrMin) {
        this.input.setAttribute('min', dataMin);
        const currentVal = parseInt(this.input.value, 10) || 0;
        if (currentVal > 0 && currentVal < dataMin) {
          this.input.value = dataMin;
        }
      }
    }

    changeQty(delta) {
      const currentVal = parseInt(this.input.value, 10) || 0;
      const attrMin = parseInt(this.input.getAttribute('min'), 10) || 0;
      const dataMin = parseInt(this.input.getAttribute('data-min'), 10) || 0;
      const min = Math.max(attrMin, dataMin);
      const newVal = Math.max(min, currentVal + delta);

      if (newVal === currentVal) return;

      this.input.value = newVal;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}